import styled from "@emotion/styled";
import CalendarIcon from '@mui/icons-material/DateRange';
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useEffect, useRef, useState } from "react";
import { useInput, useLocale } from "react-admin";
import { CustomProvider, DateRangePicker } from 'rsuite';
import { addMonths, endOfDay, endOfMonth, endOfWeek, startOfDay, startOfMonth, startOfWeek, subDays } from "date-fns";
import * as locales from 'rsuite/locales';
import { normalizeDateRange } from '../../lib/normalizeDateRange';
import 'rsuite/dist/rsuite-no-reset.min.css';
import './DateRangePicker.css';

dayjs.extend(customParseFormat);

const BetweenInput = ({ dateTime, ...props }) => {
  const { style, options } = props;
  const locale = useLocale();
  const fromField = useInput({ source: 'from' });
  const toField = useInput({ source: 'to' });
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);

  const format = dateTime ? 'dd/MM/yyyy HH:mm' : 'dd/MM/yyyy';
  const displayFormat = dateTime ? 'DD/MM/YYYY HH:mm' : 'DD/MM/YYYY';
  const parseFormat = dateTime ? ['DD/MM/YYYY HH:mm', 'D/M/YYYY HH:mm', 'DD/MM/YYYY H:mm'] : ['DD/MM/YYYY', 'D/M/YYYY'];

  const isDev = import.meta.env.DEV;

  const ranges = [
    {
      label: 'Hoje',
      value: [startOfDay(new Date()), endOfDay(new Date())]
    },
    {
      label: 'Esta semana',
      value: [startOfWeek(new Date()), endOfWeek(new Date())]
    },
    {
      label: 'Última semana',
      value: [startOfWeek(subDays(new Date(), 7)), endOfWeek(subDays(new Date(), 7))]
    },
    {
      label: 'Últimos 7 dias',
      value: [startOfDay(subDays(new Date(), 6)), endOfDay(new Date())]
    },
    {
      label: 'Este mês',
      value: [startOfMonth(new Date()), endOfMonth(new Date())]
    },
    {
      label: 'Último mês',
      value: [startOfMonth(addMonths(new Date(), -1)), endOfMonth(addMonths(new Date(), -1))]
    },
    {
      label: 'Últimos 30 dias',
      value: [startOfDay(subDays(new Date(), 29)), endOfDay(new Date())]
    },
    ...(isDev ? [
      {
        label: 'Últimos 6 meses',
        value: [startOfDay(addMonths(new Date(), -6)), endOfDay(new Date())]
      },
      {
        label: 'Último ano',
        value: [startOfDay(addMonths(new Date(), -12)), endOfDay(new Date())]
      },
    ] : []),
  ];

  const fromValue = fromField.field.value ? dayjs(fromField.field.value).format(displayFormat) : '';
  const toValue = toField.field.value ? dayjs(toField.field.value).format(displayFormat) : '';

  const [fromInputValue, setFromInputValue] = useState(fromValue);
  const [toInputValue, setToInputValue] = useState(toValue);
  const fromInputRef = useRef(null);
  const toInputRef = useRef(null);

  // Sync input values when field values change (e.g., from calendar selection)
  useEffect(() => {
    if (document.activeElement !== fromInputRef.current) {
      setFromInputValue(fromValue);
    }
  }, [fromValue]);

  useEffect(() => {
    if (document.activeElement !== toInputRef.current) {
      setToInputValue(toValue);
    }
  }, [toValue]);

  const handleFromChange = (e) => {
    setFromInputValue(e.target.value);
  };

  const handleToChange = (e) => {
    setToInputValue(e.target.value);
  };

  // Se o valor digitado inverte o intervalo, o outro campo é empurrado (o backend recusa to < from)
  // e o texto dele é ressincronizado na mão: o effect acima não mexe em input focado.
  const handleFromBlur = () => {
    const parsed = dayjs(fromInputValue, parseFormat, true);
    if (parsed.isValid()) {
      const from = (dateTime ? parsed : parsed.startOf('day')).toISOString();
      const next = normalizeDateRange({ from, to: toField.field.value }, 'from');
      fromField.field.onChange(from);
      if (next.to !== toField.field.value) {
        toField.field.onChange(next.to);
        setToInputValue(dayjs(next.to).format(displayFormat));
      }
      setFromInputValue(parsed.format(displayFormat));
    } else {
      // Reset to original value if invalid
      setFromInputValue(fromValue);
    }
  };

  const handleToBlur = () => {
    const parsed = dayjs(toInputValue, parseFormat, true);
    if (parsed.isValid()) {
      const to = (dateTime ? parsed : parsed.endOf('day')).toISOString();
      const next = normalizeDateRange({ from: fromField.field.value, to }, 'to');
      toField.field.onChange(to);
      if (next.from !== fromField.field.value) {
        fromField.field.onChange(next.from);
        setFromInputValue(dayjs(next.from).format(displayFormat));
      }
      setToInputValue(parsed.format(displayFormat));
    } else {
      // Reset to original value if invalid
      setToInputValue(toValue);
    }
  };


  return (
    <CustomProvider locale={locale === 'es' ? locales.esAR : locales.ptBR}>
      <Container style={style}>
        <InputsWrapper ref={triggerRef}>
          <InputField>
            <InputLabel>De</InputLabel>
            <InputContent>
              <TextInput
                ref={fromInputRef}
                value={fromInputValue}
                onChange={handleFromChange}
                onBlur={handleFromBlur}
                placeholder={dateTime ? 'DD/MM/AAAA HH:mm' : 'DD/MM/AAAA'}
              />
              <CalendarIcon
                sx={{ color: 'rgba(0, 0, 0, 0.54)', fontSize: 20, cursor: 'pointer' }}
                onClick={() => setOpen(true)}
              />
            </InputContent>
          </InputField>
          <InputField>
            <InputLabel>Até</InputLabel>
            <InputContent>
              <TextInput
                ref={toInputRef}
                value={toInputValue}
                onChange={handleToChange}
                onBlur={handleToBlur}
                placeholder={dateTime ? 'DD/MM/AAAA HH:mm' : 'DD/MM/AAAA'}
              />
              <CalendarIcon
                sx={{ color: 'rgba(0, 0, 0, 0.54)', fontSize: 20, cursor: 'pointer' }}
                onClick={() => setOpen(true)}
              />
            </InputContent>
          </InputField>
        </InputsWrapper>
        <HiddenPickerWrapper>
          <DateRangePicker
            open={open}
            onClose={() => setOpen(false)}
            size="lg"
            format={format}
            character=" ~ "
            showHeader={false}
            container={() => document.body}
            placement="bottomStart"
            value={[new Date(fromField.field.value), new Date(toField.field.value)]}
            shouldDisableDate={options?.disableFuture ? DateRangePicker.afterToday() : undefined}
            onChange={(value) => {
              if (!value) return;
              const [from, to] = value;
              if (dateTime) {
                if (from < to) {
                  fromField.field.onChange(dayjs(from).toISOString());
                  toField.field.onChange(dayjs(to).toISOString());
                } else {
                  fromField.field.onChange(dayjs(to).toISOString());
                  toField.field.onChange(dayjs(from).toISOString());
                }
              } else {
                fromField.field.onChange(dayjs(from).startOf('day').toISOString());
                toField.field.onChange(dayjs(to).endOf('day').toISOString());
              }
              // Update input values
              setFromInputValue(dayjs(from < to ? from : to).format(displayFormat));
              setToInputValue(dayjs(from < to ? to : from).format(displayFormat));
              setOpen(false);
            }}
            ranges={ranges}
          />
        </HiddenPickerWrapper>
      </Container>
    </CustomProvider>
  );
};

const Container = styled('div')({
  position: 'relative',
  marginBottom: '4px',
});

const InputsWrapper = styled('div')({
  display: 'flex',
  gap: '8px',
});

const InputField = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  padding: '2px 12px 4px',
  border: '1px solid rgba(0, 0, 0, 0.23)',
  borderRadius: '4px',
  minWidth: '140px',
  backgroundColor: '#fff',
  transition: 'border-color 0.2s ease',
  '&:hover': {
    borderColor: 'rgba(0, 0, 0, 0.87)',
  },
  '&:focus-within': {
    borderColor: '#79CF62',
    borderWidth: '2px',
    padding: '1px 11px 3px',
  },
});

const InputLabel = styled('span')({
  fontSize: '10px',
  color: 'rgba(0, 0, 0, 0.6)',
  marginBottom: '1px',
});

const InputContent = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
});

const TextInput = styled('input')({
  fontSize: '16px',
  color: 'rgba(0, 0, 0, 0.87)',
  border: 'none',
  outline: 'none',
  background: 'transparent',
  width: '100%',
  padding: 0,
  fontFamily: 'inherit',
  '&::placeholder': {
    color: 'rgba(0, 0, 0, 0.4)',
  },
});

const HiddenPickerWrapper = styled('div')({
  position: 'absolute',
  visibility: 'hidden',
  pointerEvents: 'none',
  width: 0,
  height: 0,
  overflow: 'hidden',
});

export const BetweenDatesTimesInput = props => <BetweenInput dateTime {...props} />;
export const BetweenDatesInput = props => <BetweenInput dateTime={false} {...props} />;
